
import { action, observable } from 'mobx';
import * as Models from 'Models/Entities';
import {
	attribute,
	entity,
	IModelAttributes,
	Model,
	ReferencePath,
} from 'Models/Model';
import UserCreditReport from 'Models/Security/Acl/UserCreditReport';
import VisitorsCreditReport from 'Models/Security/Acl/VisitorsCreditReport';
import { IAcl } from 'Models/Security/IAcl';
import * as AttrUtils from 'Util/AttributeUtils';
import {
	getCreatedModifiedCrudOptions,
	makeEnumFetchFunction,
	makeFetchOneToManyFunc,
} from 'Util/EntityUtils';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import { CRUD } from '../CRUDOptions';
import * as Enums from '../Enums';
import SuperAdministratorScheme from '../Security/Acl/SuperAdministratorScheme';


export interface ICreditReportEntityAttributes extends IModelAttributes {
	reportcontent: string;
	status: Enums.creditReportStatus;
	filepath: string;
	monitoredId?: string;
	monitored?: Models.MonitoredEntity | Models.IMonitoredEntityAttributes;
	reportRequests: Array<
		| Models.ReportRequestEntity
		| Models.IReportRequestEntityAttributes
	>;
	rawApiResponse: string;
	errorMessage: string;
	legacyId?: number;
	failureRiskScore?: Enums.riskScore;
	latePaymentRiskScore?: Enums.riskScore;
	equifaxScore?: Enums.riskScore;
}

@entity('CreditReportEntity', 'Credit Report')
export default class CreditReportEntity extends Model
	implements ICreditReportEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new UserCreditReport(),
		new VisitorsCreditReport(),

	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<CreditReportEntity, string>()
	@CRUD({
		name: 'ReportContent',
		displayType: 'textarea',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public reportcontent: string;

	@observable
	@attribute<CreditReportEntity, Enums.creditReportStatus>()
	@CRUD({
		name: 'Status',
		displayType: 'enum-combobox',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.creditReportStatusOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.creditReportStatusOptions),
		displayFunction: (attr: Enums.creditReportStatus) => Enums.creditReportStatusOptions[attr],
	})
	public status: Enums.creditReportStatus;

	@observable
	@attribute<CreditReportEntity, string>()
	@CRUD({
		name: 'FilePath',
		displayType: 'textfield',
		order: 30,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public filepath: string;

	@observable
	@attribute()
	@CRUD({
		
		name: 'Monitored',
		displayType: 'reference-combobox',
		order: 50,
		referenceTypeFunc: () => Models.MonitoredEntity,
		
	})
	public monitoredId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public monitored: Models.MonitoredEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	@CRUD({
		
		name: 'Report Requests',
		displayType: 'reference-multicombobox',
		order: 60,
		referenceTypeFunc: () => Models.ReportRequestEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'reportRequests',
			oppositeEntity: () => Models.ReportRequestEntity,
		}),
		
	})
	public reportRequests: Models.ReportRequestEntity[] = [];


	@observable
	@attribute<CreditReportEntity, string>()
	@CRUD({
		name: 'RawApiResponse',
		displayType: 'textarea',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public rawApiResponse: string;

	@observable
	@attribute<CreditReportEntity, string>()
	@CRUD({
		name: 'ErrorMessage',
		displayType: 'textarea',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public errorMessage: string;

	@observable
	@attribute<CreditReportEntity, number>()
	@CRUD({
		name: 'LegacyId',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
	})
	public legacyId?: number;

	@observable
	@attribute<CreditReportEntity, Enums.riskScore>()
	@CRUD({
		name: 'FailureRiskScore',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.riskScoreOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.riskScoreOptions),
		displayFunction: (attr: Enums.riskScore) => Enums.riskScoreOptions[attr],
	})
	public failureRiskScore?: Enums.riskScore;

	@observable
	@attribute<CreditReportEntity, Enums.riskScore>()
	@CRUD({
		name: 'LatePaymentRiskScore',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.riskScoreOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.riskScoreOptions),
		displayFunction: (attr: Enums.riskScore) => Enums.riskScoreOptions[attr],
	})
	public latePaymentRiskScore?: Enums.riskScore;

	@observable
	@attribute<CreditReportEntity, Enums.riskScore>()
	@CRUD({
		name: 'EquifaxScore',
		displayType: 'enum-combobox',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: (attr: string) => {
			return AttrUtils.standardiseEnum(attr, Enums.riskScoreOptions);
		},
		enumResolveFunction: makeEnumFetchFunction(Enums.riskScoreOptions),
		displayFunction: (attr: Enums.riskScore) => Enums.riskScoreOptions[attr],
	})
	public equifaxScore?: Enums.riskScore;

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<ICreditReportEntityAttributes>) {
		super(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<ICreditReportEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.reportcontent !== undefined) {
				this.reportcontent = attributes.reportcontent;
			}
			if (attributes.rawApiResponse !== undefined) {
				this.rawApiResponse = attributes.rawApiResponse;
			}
			if (attributes.errorMessage !== undefined) {
				this.errorMessage = attributes.errorMessage;
			}
			if (attributes.status !== undefined) {
				this.status = attributes.status;
			}
			if (attributes.filepath !== undefined) {
				this.filepath = attributes.filepath;
			}
			if (attributes.legacyId !== undefined) {
				this.legacyId = attributes.legacyId;
			}
			if (attributes.failureRiskScore !== undefined) {
				this.failureRiskScore = attributes.failureRiskScore;
			}
			if (attributes.latePaymentRiskScore !== undefined) {
				this.latePaymentRiskScore = attributes.latePaymentRiskScore;
			}
			if (attributes.equifaxScore !== undefined) {
				this.equifaxScore = attributes.equifaxScore;
			}
			if (attributes.monitoredId !== undefined) {
				this.monitoredId = attributes.monitoredId;
			}
			if (attributes.monitored !== undefined) {
				if (attributes.monitored === null) {
					this.monitored = attributes.monitored;
				} else if (attributes.monitored instanceof Models.MonitoredEntity) {
					this.monitored = attributes.monitored;
					this.monitoredId = attributes.monitored.id;
				} else {
					this.monitored = new Models.MonitoredEntity(attributes.monitored);
					this.monitoredId = this.monitored.id;
				}
			}
			if (attributes.reportRequests !== undefined && Array.isArray(attributes.reportRequests)) {
				for (const model of attributes.reportRequests) {
					if (model instanceof Models.ReportRequestEntity) {
						this.reportRequests.push(model);
					} else {
						this.reportRequests.push(new Models.ReportRequestEntity(model));
					}
				}
			}

		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	public defaultExpands = `
		monitored {
			${Models.MonitoredEntity.getAttributes().join('\n')}
		}
		reportRequests {
			${Models.ReportRequestEntity.getAttributes().join('\n')}
		}
	`;

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath: ReferencePath = {
			reportRequests: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'reportRequests',
						],
					},
				],
			},
		);
	}

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		return this.id;
	}
}

/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [createdAttr, modifiedAttr] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(CreditReportEntity.prototype, 'created');
CRUD(modifiedAttr)(CreditReportEntity.prototype, 'modified');
